import axios, { AxiosError } from 'axios'
import CryptoJS from 'crypto-js'
import { useEffect, useRef, useState } from 'react'

/**
 * Content API to get based on entries and content type
 * @param apiType - Type of the API. Expected Values : 'entries' | 'contentType'.
 * @param contentTypeId (Optional) - Content Type Id of the content.
 * @param slug (Optional) - Slug of the page.
 * @param preview (Optional) - Enables Live preview. Expected Values : 'true' | 'false'.
 */

const API_BASE_URL = process.env.REACT_APP_PWS_API_URL

// Fetch the MSAL access token from the server
const fetchAccessToken = async (paramId = 'content') => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_PWS_API_URL}/api/auth/${paramId}`)
    const accessToken = response?.data?.accessToken

    if (response?.status !== 200) {
      throw new Error(`HTTP error! Status: ${response.status}`)
    }

    if (!accessToken) {
      throw new Error(`Access token is missing in response.`)
    }
    if (paramId === 'content') {
      // Save the token and expiration info in sessionStorage
      const tokenData = {
        accessToken,
        expiresOn: new Date(response?.data?.expiresOn).getTime(), // Add expiration field from response
      }
      window.sessionStorage.setItem('msalAccessToken', JSON.stringify(tokenData))
      return accessToken
    } else {
      return accessToken
    }
  } catch (error) {
    window.sessionStorage.removeItem('msalAccessToken')
    console.error(error)
  }
}

// Function to check token validity and refresh if necessary
const getAccessToken = async () => {
  const tokenDataString = window.sessionStorage.getItem('msalAccessToken')
  if (tokenDataString) {
    const tokenData = JSON.parse(tokenDataString)
    if (tokenData.expiresOn > Date.now()) {
      return tokenData.accessToken
    } else {
      return await fetchAccessToken()
    }
  } else {
    return await fetchAccessToken()
  }
}

// Encrypting data using AES
function encryptPayload(payload, secretKey) {
  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), secretKey).toString()

  return encrypted
}

const entriesAPI = async ({ slug, preview, depth }: { slug: string; preview: boolean; depth?: number }) => {
  const msalAccessToken = await getAccessToken()
  // const options = {
  //   headers: {
  //     Authorization: `Bearer ${msalAccessToken}`,
  //   },
  // }
  const urlParams = new URLSearchParams()
  if (preview) urlParams.set('preview', 'true')
  if (depth) urlParams.set('depth', depth.toString())
  const urlParamsString = urlParams.toString()

  // return await axios.get(`${API_BASE_URL}/api/entries/${slug}${urlParamsString && '?'}${urlParamsString}`, {
  //   ...options,
  // })
// Jira - BPW -80 - 404 and 500 error handling
  const url = `${API_BASE_URL}/api/entries/${slug}${urlParamsString && '?'}${urlParamsString}`;
  const headers = {
    Authorization: `Bearer ${msalAccessToken}`,
    Accept: 'application/json',
  };
  try {
    const res = await axios.get(url, { headers });
    // 200 success path
    return { status: res.status, data: res.data };
  } catch (err) {
    const e = err as AxiosError;

    //  Capture server responses like 404/500
    if (e.response) {
      return { status: e.response.status, data: e.response.data };
    }

    //  Network or CORS    //  Network or CORS error—no response
    return { status: 0, data: null };
  }
}

const contentTypeAPI = async ({
  contentTypeId,
  preview,
  depth,
}: {
  contentTypeId: string
  preview: boolean
  depth?: number
}) => {
  const msalAccessToken = await getAccessToken()
  const options = {
    headers: {
      Authorization: `Bearer ${msalAccessToken}`,
    },
  }

  const urlParams = new URLSearchParams()
  if (preview) urlParams.set('preview', 'true')
  if (depth) urlParams.set('depth', depth.toString())
  const urlParamsString = urlParams.toString()

  return await axios.get(
    `${API_BASE_URL}/api/entries/contentType/${contentTypeId}${urlParamsString && '?'}${urlParamsString}`,
    {
      ...options,
    },
  )
}

const contentModelAPI = async ({ contentTypeId, preview }: { contentTypeId: string; preview: boolean }) => {
  const msalAccessToken = await getAccessToken()
  const options = {
    headers: {
      Authorization: `Bearer ${msalAccessToken}`,
    },
  }

  return await axios.get(`${API_BASE_URL}/api/contentModel/${contentTypeId}${preview ? '?preview=true' : ''}`, {
    ...options,
  })
}

interface ApiFilters {
  depth?: number
  contentSessionKey?: string
}

const storeContentSessionData = (key?: string, data?: unknown): void => {
  if (!key || !data) return

  // storedData to be valid for 1 hour only
  const currentDateTime = new Date()
  const expiresOn = new Date().setHours(currentDateTime.getHours() + 1)
  const storedData = {
    data,
    expiresOn,
  }
  const storedDataString = typeof data === 'string' ? data : JSON.stringify(storedData)
  window.sessionStorage.setItem(key, storedDataString)
}

/**
 *
 * @param apiType
 * @param contentTypeId
 * @param slug
 * @param preview
 * @param apiFilters {ApiFilters} filters applied to the API. use contentSessionKey for contents that is persisted in all page - e.g header, footer data
 * @returns
 */
const useContentAPI = (
  apiType: 'entries' | 'contentType' | 'contentModel',
  contentTypeId: string,
  slug: string,
  preview: boolean,
  apiFilters: ApiFilters = {},
) => {
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const prevSlug = useRef('')
  const prevContentTypeID = useRef('')

  const { depth, contentSessionKey } = apiFilters

  useEffect(() => {
    setIsLoading(true)

    if (contentSessionKey) {
      const storedData = JSON.parse(window.sessionStorage.getItem(contentSessionKey))
      if (storedData && new Date(storedData.expiresOn) > new Date()) {
        setData(storedData.data)
        setIsLoading(false)
        return
      }
    }

    if (apiType === 'entries') {
      if (prevSlug.current !== slug && prevContentTypeID.current !== contentTypeId) {
        prevSlug.current = slug
        prevContentTypeID.current = contentTypeId
        entriesAPI({ slug, preview, depth })
          .then((response) => {
            setData(response)
            storeContentSessionData(contentSessionKey, response)
          })
          .catch((err) => {
            setError(err)
          })
      }
    } else if (apiType === 'contentType') {
      if (prevContentTypeID.current !== contentTypeId) {
        prevSlug.current = slug
        prevContentTypeID.current = contentTypeId
        contentTypeAPI({ preview, contentTypeId, depth })
          .then((response) => {
            setData(response)
            storeContentSessionData(contentSessionKey, response)
          })
          .catch((err) => {
            setError(err)
          })
      }
    } else if (apiType === 'contentModel') {
      if (prevContentTypeID.current !== contentTypeId) {
        prevSlug.current = slug
        prevContentTypeID.current = contentTypeId
        contentModelAPI({ contentTypeId, preview })
          .then((response) => {
            setData(response)
            storeContentSessionData(contentSessionKey, response)
          })
          .catch((err) => {
            setError(err)
          })
      }
    }
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { data, isLoading, error }
}

const UseEmailAPI = async (payload, purposeType) => {
  try {
    const token = await fetchAccessToken(purposeType)
    const encryptedPayload = encryptPayload(payload, token)
    const options = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
    const payloadData = JSON.stringify({
      key: encryptedPayload,
    })
    const response = await axios.post(`${API_BASE_URL}/api/formSubmission/${purposeType}`, payloadData, {
      ...options,
    })

    return response
  } catch (err) {
    return err
  }
}

const UseDailyUnitPriceAPI = async () => {
  const msalAccessToken = await getAccessToken()
  try {
    const options = {
      headers: {
        Authorization: `Bearer ${msalAccessToken}`,
      },
    }
    const response = await axios.get(`${API_BASE_URL}/api/dailyUnitPrice`, {
      ...options,
    })

    return response
  } catch (err) {
    return err
  }
}

const UseVerifyReCaptchaAPI = async (payload) => {
  const msalAccessToken = await getAccessToken()
  try {
    const encryptedPayload = encryptPayload(payload, msalAccessToken)

    const options = {
      headers: {
        Authorization: `Bearer ${msalAccessToken}`,
        'Content-Type': 'application/json',
      },
    }

    const payloadData = JSON.stringify({
      key: encryptedPayload,
    })

    const response = await axios.post(`${API_BASE_URL}/api/verify/reCaptcha`, payloadData, {
      ...options,
    })

    return response
  } catch (err) {
    return err
  }
}

const GetEventsDetailsAPI = async () => {
  const msalAccessToken = await getAccessToken()
  try {
    const options = {
      headers: {
        Authorization: `Bearer ${msalAccessToken}`,
      },
    }
    const response = await axios.get(`${API_BASE_URL}/api/getEventDetails`, {
      ...options,
    })

    return response
  } catch (err) {
    return err
  }
}

const GetTaxonomiesAPI = async (query) => {
  const msalAccessToken = await getAccessToken()
  try {
    const options = {
      headers: {
        Authorization: `Bearer ${msalAccessToken}`,
      },
    }
    const response = await axios.get(`${API_BASE_URL}/api/taxonomies/${query}`, {
      ...options,
    })

    return response
  } catch (err) {
    return err
  }
}

const SaveEventsDetailsAPI = async (payload) => {
  try {
    const token = await fetchAccessToken('eventRegistration')
    const encryptedPayload = encryptPayload(payload, token)
    const options = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
    const payloadData = JSON.stringify({
      key: encryptedPayload,
    })
    const response = await axios.post(`${API_BASE_URL}/api/saveEventDetails`, payloadData, {
      ...options,
    })

    return response
  } catch (err) {
    return err
  }
}

export {
  useContentAPI,
  UseEmailAPI,
  UseDailyUnitPriceAPI,
  UseVerifyReCaptchaAPI,
  GetEventsDetailsAPI,
  SaveEventsDetailsAPI,
  GetTaxonomiesAPI,
}
